import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Paper } from '@mui/material';
import React from 'react';
import { generateGroupId, generateQuestionId } from '../../utils/faqLinkUtils';
import { StyledTypography } from '../styles/styles';
import FAQItemView from './FAQItemView';
import { FAQGroup } from './types';

interface FAQViewGroupsListProps {
  groups: FAQGroup[];
  searchTerm: string;
  expandedGroups: Set<string>;
  onGroupToggle: (groupName: string, expanded: boolean) => void;
  onQuestionExpand: (groupName: string, question: string) => void;
}

export function FAQViewGroupsList({
  groups,
  searchTerm,
  expandedGroups,
  onGroupToggle,
  onQuestionExpand,
}: FAQViewGroupsListProps) {
  if (groups.length === 0) {
    return (
      <Paper
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
        }}
      >
        <StyledTypography tone="default" fontSize="1.1rem" sx={{ color: 'text.secondary' }}>
          No FAQ items found matching "{searchTerm}". Try a different search term.
        </StyledTypography>
      </Paper>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {groups.map((group) => (
        <Accordion
          key={group.group}
          id={generateGroupId(group.group)}
          expanded={expandedGroups.has(group.group)}
          onChange={(_, expanded) => onGroupToggle(group.group, expanded)}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: '8px 0' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              '& .MuiAccordionSummary-content': { margin: '12px 0' },
            }}
          >
            <StyledTypography tone="default" fontSize="1.2rem" fontWeight={600}>
              {group.group}
            </StyledTypography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {group.items.map((item, index) => (
                <div
                  id={generateQuestionId(group.group, item.question)}
                  key={`${group.group}-${index}`}
                >
                  <FAQItemView
                    item={item}
                    expandable
                    defaultExpanded={false}
                    searchTerm={searchTerm}
                    onToggle={(expanded) => {
                      if (expanded) {
                        onQuestionExpand(group.group, item.question);
                      }
                    }}
                  />
                </div>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
